<template>
  <div class="product-price-config">
    <div class="col-md-12">
      <div class="row">
        <div class="col-md-12">
          <h4 class="section-title">Cấu hình giá theo cửa hàng</h4>
        </div>
      </div>

      <div class="row">
        <div class="col-md-12">
          <div class="d-flex justify-content-between align-items-end mb-3">
            <div class="d-flex align-items-center">
              <p class="mb-0 mr-3"><strong>Danh sách cấu hình giá theo cửa hàng</strong></p>
              <div class="d-flex align-items-center">
                <b-form-input
                  v-model="searchStoreName"
                  placeholder="Tìm kiếm theo tên cửa hàng..."
                  size="sm"
                  class="mr-2"
                  style="width: 250px;"
                  @input="filterPriceConfigs"
                ></b-form-input>
                <b-button
                  v-if="searchStoreName"
                  size="sm"
                  variant="outline-secondary"
                  @click="clearSearchStore"
                >
                  <i class="fa fa-times"></i>
                </b-button>
              </div>
            </div>
            <div class="d-flex">
              <b-button
                variant="secondary"
                size="sm"
                @click="openPriceHistoryModal"
                class="btn btn-default font-weight-bolder btn-sm mr-2"
              >
                <i class="fa fa-history icon-size"></i>
                Lịch sử cập nhật
              </b-button>
              <b-button
                v-if="priceConfigs.length > 0"
                variant="danger"
                size="sm"
                @click="showDeleteAllAlert"
                class="btn btn-danger font-weight-bolder btn-sm mr-2"
              >
                <i class="fa fa-trash icon-size"></i>
                Xóa tất cả
              </b-button>
              <b-button
                variant="primary"
                size="sm"
                @click="showAddPriceConfigModal"
                class="btn btn-primary font-weight-bolder btn-sm"
              >
                <i class="flaticon2-add-1 icon-size"></i>
                Thêm cấu hình
              </b-button>
            </div>
          </div>

          <b-table
            v-if="priceConfigs.length"
            :items="paginatedPriceConfigs"
            :fields="displayPriceConfigFields"
            class="table-bordered table-hover"
          >
            <template v-slot:cell(storeName)="row">
              <div class="text-left">
                <span>{{ row.item.storeName }}</span>
                <small v-if="row.item.storeType" class="text-muted d-block">
                  {{ getStoreTypeName(row.item.storeType) }}
                </small>
              </div>
            </template>

            <template v-slot:cell(sellingPrice)="row">
              <div class="text-right" :data-index="row.index">
                <span v-if="!row.item.isEditing">{{
                  convertPrice(row.item.sellingPrice)
                }}</span>
                <b-form-input
                  v-else
                  v-model="row.item.sellingPrice"
                  size="sm"
                  class="text-right"
                  v-mask="mask"
                  placeholder="Giá bán lẻ"
                  @keyup.enter="saveInlineEdit(row.index)"
                ></b-form-input>
              </div>
            </template>

            <template v-slot:cell(wholesalePrice)="row">
              <div class="text-right">
                <span v-if="!row.item.isEditing">{{
                  convertPrice(row.item.wholesalePrice)
                }}</span>
                <b-form-input
                  v-else
                  v-model="row.item.wholesalePrice"
                  size="sm"
                  class="text-right"
                  v-mask="mask"
                  placeholder="Giá bán buôn"
                  @keyup.enter="saveInlineEdit(row.index)"
                ></b-form-input>
              </div>
            </template>

            <template v-slot:cell(listedPrice)="row">
              <div class="text-right">
                <span v-if="!row.item.isEditing">{{
                  convertPrice(row.item.listedPrice)
                }}</span>
                <b-form-input
                  v-else
                  v-model="row.item.listedPrice"
                  size="sm"
                  class="text-right"
                  v-mask="mask"
                  placeholder="Giá niêm yết"
                  @keyup.enter="saveInlineEdit(row.index)"
                ></b-form-input>
              </div>
            </template>

            <template v-slot:cell(updateChildProducts)="row" v-if="!isChildrenProduct">
              <div class="d-flex justify-content-center align-items-center p-0">
                <span v-if="!row.item.isEditing">
                  <i :class="row.item.updateChildProducts ? 'fa fa-check text-success' : 'fa fa-times text-muted'"></i>
                </span>
                <b-form-checkbox
                class="ml-2"
                  v-else
                  v-model="row.item.updateChildProducts"
                  size="lg"
                  @change="updateChildProductsChanged(row.index)"
                >
                </b-form-checkbox>
              </div>
            </template>

            <template v-slot:cell(actions)="row">
              <div class="d-flex justify-content-center">
                <v-icon
                  small
                  class="mr-2 text-warning"
                  @click="toggleEditMode(row.index, row.item)"
                  v-b-tooltip
                  :title="row.item.isEditing ? 'Lưu' : 'Cập nhật'"
                >
                  {{ row.item.isEditing ? 'mdi-check' : 'mdi-pencil' }}
                </v-icon>
                <v-icon
                  v-if="!row.item.isEditing"
                  small
                  class="mr-2 text-danger"
                  @click="showDeletePriceConfigAlert(row.item)"
                  v-b-tooltip
                  title="Xóa"
                >
                  mdi-delete
                </v-icon>
                <v-icon
                  v-if="row.item.isEditing"
                  small
                  class="mr-2 text-secondary"
                  @click="cancelEdit(row.index)"
                  v-b-tooltip
                  title="Hủy"
                >
                  mdi-close
                </v-icon>
              </div>
            </template>
          </b-table>

          <div v-else class="text-center py-4">
            <p class="text-muted">Chưa có cấu hình giá nào</p>
          </div>

          <!-- Pagination -->
          <div v-if="priceConfigs.length > perPage" class="d-flex justify-content-between align-items-center mt-3">
            <div>
              <p class="mb-0 text-muted">
                Hiển thị {{ (currentPage - 1) * perPage + 1 }} - {{ Math.min(currentPage * perPage, priceConfigs.length) }} 
                trong tổng số {{ priceConfigs.length }} cấu hình giá
              </p>
            </div>
            <b-pagination
              v-model="currentPage"
              :total-rows="priceConfigs.length"
              :per-page="perPage"
              size="sm"
              class="mb-0"
              first-number
              last-number
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Modal thêm/cập nhật cấu hình giá -->
    <b-modal
      ref="price-config-modal"
      hide-footer
      :title="isEditing ? 'Cập nhật cấu hình giá' : 'Thêm cấu hình giá'"
      v-model="showPriceConfigModal"
      size="lg"
    >
      <v-form ref="priceConfigForm" lazy-validation>
        <div class="col-md-12">
          <div class="row">
            <div class="col-md-4">
              <b-form-group label="Lọc theo loại cửa hàng:" class="mb-0">
                <div class="d-flex align-items-end">
                  <b-form-select
                    v-model="selectedStoreType"
                    :options="storeTypeOptions"
                    size="sm"
                    placeholder="Chọn loại cửa hàng"
                    class="flex-grow-1 mr-2"
                  ></b-form-select>
                  <b-button
                    v-if="selectedStoreType"
                    size="sm"
                    variant="outline-danger"
                    @click="clearStoreTypeFilter"
                    class="d-flex justify-content-center align-items-center"
                  >
                    <i class="fa fa-times px-0"></i>
                  </b-button>
                </div>
                <small class="text-muted d-flex align-items-center mt-2">
                  <i class="fa fa-info-circle mr-1"></i>
                  Lọc để hiển thị cửa hàng theo loại
                </small>
              </b-form-group>
            </div>
            <div class="col-md-8">
              <b-form-group label="Cửa hàng:" class="required-control">
                <div class="position-relative">
                  <BDropdownCustom
                    v-model="selectedStores"
                    :options="storeOptionsForDropdown"
                    :placeholder="'cửa hàng'"
                    :textProp="'label'"
                    :valueProp="'id'"
                    :matchKey="['label', 'shortName']"
                    :searchable="true"
                    :limit="10"
                    :showResizeHorizontal="false"
                  />
                </div>
                <b-form-invalid-feedback>
                  Vui lòng chọn ít nhất một cửa hàng.
                </b-form-invalid-feedback>
              </b-form-group>
            </div>
          </div>
          <hr class="mb-5 pb-5" />
          <div class="row">
            <div class="col-md-6">
              <b-form-group label="Giá bán lẻ:">
                <b-form-input
                  size="sm"
                  v-model="priceConfig.sellingPrice"
                  placeholder="Nhập giá bán lẻ"
                  class="text-right"
                  v-mask="mask"
                ></b-form-input>
              </b-form-group>
            </div>
            <div class="col-md-6">
              <b-form-group label="Giá bán buôn:">
                <b-form-input
                  size="sm"
                  v-model="priceConfig.wholesalePrice"
                  placeholder="Nhập giá bán buôn"
                  class="text-right"
                  v-mask="mask"
                ></b-form-input>
              </b-form-group>
            </div>
            <div class="col-md-6">
              <b-form-group label="Giá niêm yết:">
                <b-form-input
                  size="sm"
                  v-model="priceConfig.listedPrice"
                  placeholder="Nhập giá niêm yết"
                  class="text-right"
                  v-mask="mask"
                ></b-form-input>
              </b-form-group>
            </div>
          </div>
          <div class="row" v-if="!isChildrenProduct">
            <div class="col-md-12">
              <b-form-group>
                <b-form-checkbox
                  v-model="priceConfig.updateChildProducts"
                  size="lg"
                >
                  <strong>Cập nhật giá sản phẩm con theo cha</strong>
                </b-form-checkbox>
                <small class="text-muted d-block mt-1">
                  <i class="fa fa-info-circle mr-1"></i>
                  Khi bật, giá sẽ được áp dụng cho tất cả sản phẩm con của sản phẩm này
                </small>
              </b-form-group>
            </div>
          </div>
        </div>
      </v-form>

      <div class="d-flex justify-content-end mt-3">
        <b-button
          variant="primary"
          size="sm"
          @click="savePriceConfig"
          style="font-weight: 600; width: 70px"
        >
          {{ isEditing ? 'Cập nhật' : 'Thêm' }}
        </b-button>
        <b-button
          variant="secondary"
          size="sm"
          @click="hidePriceConfigModal"
          style="margin-left: 10px; font-weight: 600; width: 70px"
        >
          Hủy
        </b-button>
      </div>
    </b-modal>

    <!-- Modal lịch sử cập nhật giá -->
    <ProductPriceHistoryModal
      :productId="productId"
      :show="showPriceHistoryModal"
      @update:show="showPriceHistoryModal = $event"
    />
  </div>
</template>

<script>
import ApiService from '@/core/services/api.service';
import { convertPrice, currencyMask, unMaskPrice, fullTextSearch } from '@/utils/common';
import { getToastInstance } from '@/utils/toastHelper';
import { showDeleteAlert } from '@/utils/sweet-alert2';
import { PRICE_CONFIG_FIELDS, DEFAULT_PRICE_CONFIG } from './constants';
import BDropdownCustom from '@/view/base/bootstrap/BDropdownCustom.vue';
import { STORE_TYPE_OPTIONS } from '@/utils/enum-name';
import { StoreOption } from '@/view/pages/feature-flags/models/StoreOption';
import ProductPriceHistoryModal from './ProductPriceHistoryModal.vue';

export default {
  name: 'ProductPriceConfig',
  components: {
    BDropdownCustom,
    ProductPriceHistoryModal,
  },
  props: {
    productId: {
      type: String,
      default: null,
    },
    isChildrenProduct: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      priceConfigs: [],
      originalPriceConfigs: [],
      storeTreeOptions: [],
      allStores: [],
      selectedStores: [],
      storeOptionsForDropdown: [],
      showPriceConfigModal: false,
      isEditing: false,
      editingIndex: -1,
      priceConfig: { ...DEFAULT_PRICE_CONFIG },
      priceConfigFields: PRICE_CONFIG_FIELDS,
      mask: currencyMask,
      toast: null,
      selectedStoreType: null,
      storeTypeOptions: STORE_TYPE_OPTIONS,
      searchStoreName: '', // Search store name
      // Lịch sử cập nhật giá
      showPriceHistoryModal: false,
      // Pagination
      currentPage: 1,
      perPage: 10,
      totalRows: 0
    };
  },
  computed: {
    validateStoresState() {
      return this.selectedStores && this.selectedStores.length > 0
        ? true
        : false;
    },
    filteredStoreTreeOptions() {
      let filteredOptions = this.storeTreeOptions;

      // Lọc theo loại cửa hàng
      if (this.selectedStoreType) {
        filteredOptions = this.storeTreeOptions.filter((option) => {
          const store = this.allStores.find((s) => s.id === option.id);
          return store && store.storeType === this.selectedStoreType;
        });
      }

      return filteredOptions;
    },
    displayPriceConfigFields() {
      if (!this.isChildrenProduct) {
        return this.priceConfigFields;
      }
      // Loại bỏ cột updateChildProducts khi là sản phẩm con
      return this.priceConfigFields.filter(field => field.key !== 'updateChildProducts');
    },
    paginatedPriceConfigs() {
      const start = (this.currentPage - 1) * this.perPage;
      const end = start + this.perPage;
      return this.priceConfigs.slice(start, end);
    },
  },
  watch: {
    selectedStoreType() {
      this.updateStoreOptionsForDropdown();
    },
  },
  created() {
    this.toast = getToastInstance();
    this.fetchStores();
    if (this.productId) {
      this.fetchPriceConfigs();
    }
    console.log({ isChildrenProduct: this.isChildrenProduct });
  },
  methods: {
    convertPrice,
    getStoreTypeName(storeType) {
      const storeTypeOption = this.storeTypeOptions.find(
        (option) => option.value === storeType,
      );
      return storeTypeOption ? storeTypeOption.text : '';
    },
    clearStoreTypeFilter() {
      this.selectedStoreType = null;
      this.selectedStores = [];
      this.updateStoreOptionsForDropdown();
    },
    updateStoreOptionsForDropdown() {
      let filteredOptions = this.filteredStoreTreeOptions;
      
      const selectedStoresNotInFilter = this.selectedStores.filter(
        (storeId) => {
          const store = this.allStores.find((s) => s.id === storeId);
          return store && store.storeType !== this.selectedStoreType;
        },
      );

      const additionalOptions = this.storeTreeOptions.filter((option) =>
        selectedStoresNotInFilter.includes(option.id),
      );

      this.storeOptionsForDropdown = [...filteredOptions, ...additionalOptions];
    },
    fetchStores() {
      ApiService.get('/stores/getStores')
        .then((response) => {
          this.allStores = response.data.data;
          this.storeTreeOptions = response.data.data.map(
            (store) => new StoreOption(store),
          );
          this.updateStoreOptionsForDropdown();
        })
        .catch((error) => {
          console.error('Error fetching stores:', error);
          this.toast.makeToastFailure('Không thể tải danh sách cửa hàng');
          this.storeTreeOptions = [];
          this.storeOptionsForDropdown = [];
        });
    },
    fetchPriceConfigs() {
      if (!this.productId) return;

      const params = {
        productId: this.productId,
        getFull: true,
      };

      ApiService.query(`/product-price-configs`, { params })
        .then((response) => {
          const configs = (response.data.data || []).map((item) => ({
            ...item,
            storeName: item.store?.name || '',
            updateChildProducts: item.updateChildProducts || false,
          }));
          this.originalPriceConfigs = [...configs]; // Lưu danh sách gốc
          this.priceConfigs = [...configs];
        })
        .catch((error) => {
          console.error('Error fetching price configs:', error);
          this.priceConfigs = [];
          this.originalPriceConfigs = [];
        });
    },
    showAddPriceConfigModal() {
      this.isEditing = false;
      this.editingIndex = -1;
      this.resetPriceConfig();
      this.showPriceConfigModal = true;
    },
    hidePriceConfigModal() {
      this.showPriceConfigModal = false;
      this.resetPriceConfig();
    },
    resetPriceConfig() {
      this.priceConfig = { ...DEFAULT_PRICE_CONFIG };
      this.selectedStores = [];
      this.selectedStoreType = null;
      this.searchStoreName = '';
    },
    editPriceConfig(index, item) {
      this.isEditing = true;
      this.editingIndex = index;
      this.selectedStores = [item.storeId];
      this.priceConfig = { 
        ...item,
        updateChildProducts: item.updateChildProducts || false
      };
      this.showPriceConfigModal = true;
    },
    savePriceConfig() {
      if (!this.selectedStores || this.selectedStores.length === 0) {
        this.toast.makeToastFailure('Vui lòng chọn ít nhất một cửa hàng');
        return;
      }

      const newPriceConfigs = this.selectedStores.map((store) => {
        const selectedStore = this.allStores.find((s) => s.id === store.id);
        
        return {
          storeId: selectedStore.id,
          storeName: selectedStore.name,
          storeType: selectedStore.storeType,
          sellingPrice: this.priceConfig.sellingPrice
            ? unMaskPrice(this.priceConfig.sellingPrice)
            : 0,
          wholesalePrice: this.priceConfig.wholesalePrice
            ? unMaskPrice(this.priceConfig.wholesalePrice)
            : 0,
          listedPrice: this.priceConfig.listedPrice
            ? unMaskPrice(this.priceConfig.listedPrice)
            : 0,
          updateChildProducts: this.priceConfig.updateChildProducts || false,
          isEditing: false,
        };
      });

      if (this.isEditing) {
        this.priceConfigs[this.editingIndex] = newPriceConfigs[0];
        // Cập nhật originalPriceConfigs khi edit
        const originalIndex = this.originalPriceConfigs.findIndex(
          (pc) => pc.storeId === newPriceConfigs[0].storeId,
        );
        if (originalIndex !== -1) {
          this.originalPriceConfigs[originalIndex] = newPriceConfigs[0];
        }
      } else {
        newPriceConfigs.forEach((newConfig) => {
          const existingIndex = this.priceConfigs.findIndex(
            (pc) => pc.storeId === newConfig.storeId,
          );
          if (existingIndex !== -1) {
            this.toast.makeToastFailure(
              `Cửa hàng "${newConfig.storeName}" đã được cấu hình giá!`,
            );
            return;
          }
          this.priceConfigs.push(newConfig);
          this.originalPriceConfigs.push(newConfig);
        });
      }

      this.hidePriceConfigModal();
    },
    showDeletePriceConfigAlert(item) {
      showDeleteAlert(
        'Xóa cấu hình giá!',
        `Bạn có chắc muốn xóa cấu hình giá cho cửa hàng "${item.storeName}"?`,
        item,
        this.deletePriceConfig,
      );
    },
    deletePriceConfig(item) {
      const index = this.priceConfigs.findIndex(
        (pc) => pc.storeId === item.storeId,
      );
      if (index !== -1) {
        this.priceConfigs.splice(index, 1);
      }
      
      // Xóa khỏi originalPriceConfigs để đồng bộ
      const originalIndex = this.originalPriceConfigs.findIndex(
        (pc) => pc.storeId === item.storeId,
      );
      if (originalIndex !== -1) {
        this.originalPriceConfigs.splice(originalIndex, 1);
      }
    },
    getPriceConfigs() {
      return this.priceConfigs.map((config) => ({
        storeId: config.storeId,
        listedPrice: config.listedPrice || 0,
        sellingPrice: config.sellingPrice || 0,
        wholesalePrice: config.wholesalePrice || 0,
        updateChildProducts: config.updateChildProducts || false,
      }));
    },

    toggleEditMode(index, item) {
      if (item.isEditing === true) {
        this.saveInlineEdit(index);
      } else {
        this.startInlineEdit(index, item);
      }
    },

    startInlineEdit(index, item) {
      // Tính toán index thực tế trong toàn bộ danh sách
      const actualIndex = (this.currentPage - 1) * this.perPage + index;
      
      this.priceConfigs[actualIndex].originalData = {
        sellingPrice: item.sellingPrice,
        wholesalePrice: item.wholesalePrice,
        listedPrice: item.listedPrice,
        updateChildProducts: item.updateChildProducts,
      };

      this.priceConfigs[actualIndex].isEditing = true;

      this.priceConfigs[actualIndex].sellingPrice = this.convertPrice(
        item.sellingPrice,
      );
      this.priceConfigs[actualIndex].wholesalePrice = this.convertPrice(
        item.wholesalePrice,
      );
      this.priceConfigs[actualIndex].listedPrice = this.convertPrice(
        item.listedPrice,
      );

      this.$nextTick(() => {
        const firstInput = document.querySelector(
          `[data-index="${index}"] input`,
        );
        if (firstInput) {
          firstInput.focus();
        }
      });
    },

    saveInlineEdit(index) {
      const actualIndex = (this.currentPage - 1) * this.perPage + index;
      const item = this.priceConfigs[actualIndex];

      const sellingPrice = item.sellingPrice
        ? unMaskPrice(item.sellingPrice)
        : 0;
      const wholesalePrice = item.wholesalePrice
        ? unMaskPrice(item.wholesalePrice)
        : 0;
      const listedPrice = item.listedPrice ? unMaskPrice(item.listedPrice) : 0;

      item.sellingPrice = sellingPrice;
      item.wholesalePrice = wholesalePrice;
      item.listedPrice = listedPrice;
      item.isEditing = false;

      delete item.originalData;
      
      // Cập nhật originalPriceConfigs để đồng bộ
      const originalIndex = this.originalPriceConfigs.findIndex(
        (pc) => pc.storeId === item.storeId,
      );
      if (originalIndex !== -1) {
        this.originalPriceConfigs[originalIndex] = { ...item };
      }
    },

    cancelEdit(index) {
      const actualIndex = (this.currentPage - 1) * this.perPage + index;
      const item = this.priceConfigs[actualIndex];

      if (item.originalData) {
        item.sellingPrice = item.originalData.sellingPrice;
        item.wholesalePrice = item.originalData.wholesalePrice;
        item.listedPrice = item.originalData.listedPrice;
        item.updateChildProducts = item.originalData.updateChildProducts;
        delete item.originalData;
      }

      item.isEditing = false;
    },

    clearAllSelectedStores() {
      this.selectedStores = [];
    },
    
    filterPriceConfigs() {
      this.priceConfigs = fullTextSearch(
        this.originalPriceConfigs,
        this.searchStoreName,
        ['storeName', 'storeId'],
        {
          caseSensitive: false,
          exactMatch: false,
          includeAccent: true,
          minLength: 1
        }
      );
      this.currentPage = 1; // Reset về trang đầu khi filter
    },
    
    clearSearchStore() {
      this.searchStoreName = '';
      this.priceConfigs = [...this.originalPriceConfigs];
      this.currentPage = 1;
    },
    
    updateChildProductsChanged(index) {
      const actualIndex = (this.currentPage - 1) * this.perPage + index;
      
      // Cập nhật originalPriceConfigs để đồng bộ khi checkbox thay đổi
      const originalIndex = this.originalPriceConfigs.findIndex(
        (pc) => pc.storeId === this.priceConfigs[actualIndex].storeId,
      );
      if (originalIndex !== -1) {
        this.originalPriceConfigs[originalIndex].updateChildProducts = this.priceConfigs[actualIndex].updateChildProducts;
      }
    },

    openPriceHistoryModal() {
      this.showPriceHistoryModal = true;
    },
    showDeleteAllAlert() {
      showDeleteAlert(
        'Xóa tất cả cấu hình giá!',
        `Bạn có chắc muốn xóa tất cả ${this.priceConfigs.length} cấu hình giá cho sản phẩm này? Hành động này không thể hoàn tác.`,
        null,
        this.deleteAllPriceConfigs,
      );
    },
    deleteAllPriceConfigs() {
      this.priceConfigs = [];
      this.originalPriceConfigs = [];
      this.currentPage = 1;
    },
    validateBeforeSubmit() {
      const editingItems = this.priceConfigs.filter((item) => item.isEditing);
      if (editingItems.length > 0) {
        this.toast && this.toast.makeToastFailure('Vui lòng lưu các dòng đang chỉnh sửa cấu hình giá sản phẩm!');
        return false;
      }

      const hasNegative = this.priceConfigs.some((item) => {
        const selling = Number(item.sellingPrice || 0);
        const wholesale = Number(item.wholesalePrice || 0);
        const listed = Number(item.listedPrice || 0);
        return selling < 0 || wholesale < 0 || listed < 0;
      });

      if (hasNegative) {
        this.toast && this.toast.makeToastFailure('Giá trong cấu hình cửa hàng không được âm');
        return false;
      }
      return true;
    },
  },
};
</script>
